import { Role } from '../enums/role.enum';
import { LookupItem, UserRef } from './common';

/**
 * T81 — Role-aware dashboard contract (openapi.yaml v1.5.0, additive bump).
 *
 * Decision D (endpoint shape): per-widget-GROUP independent fetches under a `/dashboard/*`
 * namespace — `GET /dashboard/summary` (the scalar stat cards, one cheap parallel-SQL call that
 * succeeds/fails as a unit) and `GET /dashboard/activity` (the recent-activity feed, always a
 * separate endpoint). The stat-card group and the feed are isolated: one failing never blanks the
 * other, satisfying the §4 "one widget failing must never blank the dashboard" rule at two-group
 * granularity. Both endpoints are role-parameterized — the SAME controller resolves the caller's
 * role and returns that role's set, so the other 6 roles are additive later with no redesign. Only
 * Admin is implemented this pass (`x-roles: [Admin]`, RolesGuard → non-Admin gets 403).
 *
 * Decision E (activity-feed source): read-time bounded UNION across the audited business tables
 * (students, leads, student_affairs, payment_plans, installments, exam_bookings). No audit-log
 * table, no new write path. See DashboardActivityItem.
 *
 * All "today"/"this week" boundaries are computed in Africa/Cairo (DASHBOARD_TIMEZONE), never UTC.
 */

/** IANA tz every dashboard boundary (today / this week) is computed in. Africa/Cairo, per T80. */
export const DASHBOARD_TIMEZONE = 'Africa/Cairo';

/**
 * Admin's 5 scalar stat cards. Adding a role later = adding that role's keys here + a backend
 * branch + a frontend registry entry — no shape change. The frontend registry maps each key to its
 * localized title + icon (presentation stays frontend-side); the backend supplies value + deepLink.
 */
export const DASHBOARD_CARD_KEYS = [
  'totalStudents', // Widget 1 — COUNT students, deletedAt IS NULL, no filter.
  'newLeadsToday', // Widget 2 — COUNT leads created today (Cairo), any status/source, not deleted.
  'pendingAccounts', // Widget 3 — COUNT(DISTINCT student) with >=1 overdue installment on the Active plan.
  'documentsIncomplete', // Widget 4 — COUNT student-affairs files with documentsReceived < 10.
  'examsBookedThisWeek', // Widget 5 — COUNT exam sections Booked/Completed with examDate in the Cairo week.
] as const;
export type DashboardCardKey = (typeof DASHBOARD_CARD_KEYS)[number];

/** Entity types the activity feed can surface. Users/lookups are EXCLUDED (T80 ruling Q4). */
export const DASHBOARD_ACTIVITY_ENTITY_TYPES = [
  'student',
  'lead',
  'studentAffairs',
  'paymentPlan', // covers payment_plans AND installments rows (installment edits roll up to the plan).
  'examBooking',
] as const;
export type DashboardActivityEntityType = (typeof DASHBOARD_ACTIVITY_ENTITY_TYPES)[number];

/** created_at == updated_at → 'created', else 'edited' (T80 widget 6 classification rule). */
export const DASHBOARD_ACTIVITY_ACTIONS = ['created', 'edited'] as const;
export type DashboardActivityAction = (typeof DASHBOARD_ACTIVITY_ACTIONS)[number];

/**
 * T98 — optional period selector for GET /dashboard/summary (openapi.yaml v1.6.0, additive bump).
 *
 * A single value that, when supplied, scopes BOTH date-scoped cards (`newLeadsToday`,
 * `examsBookedThisWeek`) to the SAME Cairo calendar window. The 3 live cards (`totalStudents`,
 * `pendingAccounts`, `documentsIncomplete`) ignore it entirely.
 *
 * ABSENCE of `period` = the pre-T98 behavior, byte-for-byte: `newLeadsToday` = Cairo today,
 * `examsBookedThisWeek` = the current Sat..Fri Cairo week (two INDEPENDENT windows), and NO
 * `resolvedPeriod` echo is emitted (the field is omitted from the JSON, not null). This is the hard
 * backward-compat gate — do not emit `resolvedPeriod` unless a `period` query param was supplied.
 *
 * - `today`      → [cairoToday, cairoToday]
 * - `this-week`  → the current Sat..Fri Cairo week (cairoWeekBounds)
 * - `this-month` → [first Cairo day of the current month, last Cairo day of the current month]
 * - `this-year`  → [Jan 1 (Cairo), Dec 31 (Cairo)] of the current Cairo year
 * - `custom`     → [from, to] exactly as supplied (both REQUIRED, from ≤ to, span ≤ 366 days)
 */
export const DASHBOARD_SUMMARY_PERIODS = ['today', 'this-week', 'this-month', 'this-year', 'custom'] as const;
export type DashboardSummaryPeriod = (typeof DASHBOARD_SUMMARY_PERIODS)[number];

/** Max inclusive span (days) of a `period=custom` range — `to − from ≤ 365` day-steps, i.e. up to
 * 366 calendar days counting both endpoints (T97 §4). */
export const DASHBOARD_CUSTOM_RANGE_MAX_DAYS = 366;

/**
 * The `GET /dashboard/summary` query contract (T98). Every field is OPTIONAL at the transport level;
 * cross-field rules below are enforced by the backend DTO (T99) and surface as the standard 400
 * `VALIDATION_ERROR` envelope with `details: [{ field, message }]` (no new error code):
 *
 *  R1 `period`        — must be one of DASHBOARD_SUMMARY_PERIODS               → field `period`
 *  R2 `from`/`to`     — when `period='custom'`, BOTH are required + valid dates → field `from`/`to`
 *  R3 `from`/`to`     — supplying either while `period ≠ 'custom'` is an error  → field `from`/`to`
 *  R4 `from`/`to`     — malformed (not `YYYY-MM-DD`) is an error                → field `from`/`to`
 *  R5 range order     — `from ≤ to`                                            → field `to`
 *  R6 range span      — inclusive span ≤ DASHBOARD_CUSTOM_RANGE_MAX_DAYS       → field `to`
 *
 * The frontend (T100) builds this; it never computes Cairo boundaries — it reads them back from
 * `DashboardSummaryResponse.resolvedPeriod`.
 */
export interface DashboardSummaryQueryParams {
  /** Omit for pre-T98 behavior (independent today/this-week windows, no echo). */
  period?: DashboardSummaryPeriod;
  /** Cairo calendar date `YYYY-MM-DD`. ONLY valid when `period='custom'` (required then). */
  from?: string;
  /** Cairo calendar date `YYYY-MM-DD`. ONLY valid when `period='custom'` (required then). */
  to?: string;
}

/**
 * openapi.yaml components.schemas.ResolvedPeriod — the server-resolved Cairo window echoed back so
 * the frontend renders the period-context label ("This month: 1–31 Jul 2026") and the two scoped
 * cards' deep-links WITHOUT ever recomputing a Cairo boundary client-side (T97 open-q #4).
 * `from`/`to` are INCLUSIVE Cairo calendar dates (`YYYY-MM-DD`); for `period='today'` `from === to`.
 */
export interface ResolvedPeriod {
  period: DashboardSummaryPeriod;
  /** Inclusive lower bound, Cairo `YYYY-MM-DD`. */
  from: string;
  /** Inclusive upper bound, Cairo `YYYY-MM-DD`. */
  to: string;
}

/**
 * openapi.yaml components.schemas.DashboardStatCard — one scalar stat widget.
 * `deepLink` is a relative SPA path (no host, no `/api` prefix) INCLUDING the exact filter params
 * that reproduce the count (e.g. the Cairo week the exam count used). The backend emits it so the
 * filtered list can never drift from the number shown — the frontend never recomputes Cairo
 * boundaries. Presentation (title/icon) is resolved frontend-side from `key`.
 */
export interface DashboardStatCard {
  key: DashboardCardKey;
  /** Non-negative integer. A legitimate 0 renders the number, NOT an empty state (T80). */
  value: number;
  /** e.g. '/students', '/leads?createdFrom=2026-07-14&createdTo=2026-07-14',
   * '/accounts?overdueOnly=true', '/student-affairs?completeness=incomplete',
   * '/exam-bookings?examDateFrom=2026-07-11&examDateTo=2026-07-17&bookingStatuses=Booked,Completed'. */
  deepLink: string;
}

/** openapi.yaml components.schemas.DashboardSummaryResponse — the stat-card widget GROUP. */
export interface DashboardSummaryResponse {
  /** The caller's role — the set of `cards` is this role's set (role-parameterized). */
  role: Role;
  /** IANA tz the boundaries were computed in — always DASHBOARD_TIMEZONE ('Africa/Cairo'). */
  timezone: string;
  /** Server ISO-8601 instant the counts were computed. */
  generatedAt: string;
  /** Admin → 5 cards in DASHBOARD_CARD_KEYS order. Other roles → their own (or empty) set. */
  cards: DashboardStatCard[];
  /**
   * T98 additive echo — present ONLY when a `period` query param was supplied. When absent (the
   * default, pre-T98 request), this field is OMITTED entirely (not null) so the response is
   * byte-for-byte identical to v1.5.0. When present, BOTH `newLeadsToday` and `examsBookedThisWeek`
   * were counted over this exact Cairo window (and their deep-links carry it). The frontend reads
   * the window from here and never recomputes a Cairo boundary.
   */
  resolvedPeriod?: ResolvedPeriod;
  /**
   * T170 (§20, DEC-BR13/FR-44, architecture §6.2, `docs/architecture/18-branches-contract.md` §2.5) —
   * the per-branch breakdown. OMITTED entirely (never `null`, never `[]`) unless the caller's
   * EFFECTIVE branch scope spans 2+ branches (DEC-BRC10 — the `resolvedPeriod` omission precedent, so
   * a single-branch academy's response stays byte-for-byte v1.12.0). Two ways to reach 2+: a
   * branch-scoped user assigned 2+ branches, OR an all-branches user when 2+ ACTIVE branches exist
   * (DEC-BRA15 — otherwise the breakdown is unreachable, since Admin is the only dashboard role
   * today and Admin is always all-branches). A single-branch (scoped or all-branches-with-1-active)
   * caller gets none — it would just duplicate the top-level `cards`.
   */
  byBranch?: DashboardBranchBreakdown[];
}

/**
 * openapi.yaml components.schemas.DashboardBranchBreakdown — one branch's row in the DEC-BR13
 * per-branch breakdown. `cards` carries the SAME keys, in the SAME order, as the top-level `cards`
 * (never a shorter/different set) so the `branchA + branchB === all` invariant can be asserted
 * mechanically per key. Each `deepLink` is the top-level card's own link with `&branch=<id>`
 * (or `?branch=<id>` when the base link carries no query string) appended server-side — the
 * frontend never builds it (the existing dashboard deep-link convention).
 */
export interface DashboardBranchBreakdown {
  branch: LookupItem;
  cards: DashboardStatCard[];
}

/**
 * openapi.yaml components.schemas.DashboardActivityItem — one row in the recent-activity feed.
 * Source: read-time UNION over the audited tables (Decision E). `at` = GREATEST(created_at,
 * updated_at). `actor` = resolve(updated_by ?? created_by) via batched UserRef lookup; NULL (no
 * stamp, or user not found) → the frontend renders "System" / "النظام". `entityLabel` is the
 * subject's display name — student-scoped entities carry the student's Arabic name (UI-primary per
 * Q4); leads carry `fullName`; frontend wraps it in <bdi>. The type noun is localized frontend-side
 * from `entityType`. `deepLink` targets the entity's detail page (relative SPA path).
 */
export interface DashboardActivityItem {
  entityType: DashboardActivityEntityType;
  /** The audited row's PK (char(36) uuid for lead; studentCode-derived path for the rest). */
  entityId: string;
  /** Student Code for student-scoped entities (student/affairs/plan/exam); null for a lead. */
  studentCode: string | null;
  /** Display name of the subject (student Arabic name, or lead fullName). Frontend <bdi>-wraps. */
  entityLabel: string;
  action: DashboardActivityAction;
  /** null → render as "System" (no actor stamped / actor not resolvable). */
  actor: UserRef | null;
  /** ISO-8601 = GREATEST(created_at, updated_at). */
  at: string;
  /** Relative SPA path to the entity's detail, e.g. '/students/FA-2026-0001', '/leads/<uuid>'. */
  deepLink: string;
}

/** openapi.yaml components.schemas.DashboardActivityResponse — exactly up to 10 items, newest first. */
export interface DashboardActivityResponse {
  /** <= 10 items, ordered by `at` DESC with a deterministic tiebreak (entityType asc, entityId asc). */
  items: DashboardActivityItem[];
  generatedAt: string;
}
